import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import {
  LayoutDashboard,
  ClipboardList,
  FileCheck,
  CheckSquare,
  MessageSquare,
  FileText,
  Bell,
  Menu,
  X,
  LogOut,
  User,
  Award,
  Settings,
  CheckCircle,
  Shield,
  Truck,
  AlertTriangle,
  Factory,
  TrendingUp,
  Users,
  GitBranch,
  Wrench,
  ChevronDown,
  ChevronRight,
  Command,
  FolderOpen,
  Mail
} from 'lucide-react';
 
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [alertCount, setAlertCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [showAlertDropdown, setShowAlertDropdown] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    quality: true,
    compliance: true,
    operations: false,
    admin: false
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.role === 'qa_manager' || user?.role === 'manager';
 
  // Fetch alerts for admin users
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchAlerts = async () => {
      try {
        // Fetch quality management alerts
        const response = await apiClient.get('/quality-alerts');
        const alertData = response.data || [];
        const unread = alertData.filter(a => !a.dismissed);
        setAlerts(unread.slice(0, 10)); // Show top 10
        setAlertCount(unread.length);
      } catch (e) {
        // Fallback to generate alerts
        try {
          await apiClient.post('/quality-alerts/generate');
          const response = await apiClient.get('/quality-alerts');
          const alertData = response.data || [];
          const unread = alertData.filter(a => !a.dismissed);
          setAlerts(unread.slice(0, 10));
          setAlertCount(unread.length);
        } catch (err) {
          // Silently fail
        }
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);
 
  const handleDismissAlert = async (alertId) => {
    try {
      await apiClient.put(`/quality-alerts/${alertId}/dismiss`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setAlertCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to dismiss alert');
    }
  };

  const handleClearAllAlerts = async () => {
    try {
      await apiClient.post('/quality-alerts/clear-all');
      setAlerts([]);
      setAlertCount(0);
      setShowAlertDropdown(false);
    } catch (e) {
      console.error('Failed to clear alerts');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
 
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };
 
  // Navigation organized by QMS functional areas
  const navGroups = [
    {
      id: 'overview',
      items: [
        { name: 'Command Center', href: '/command-center', icon: LayoutDashboard, roles: ['admin', 'manager', 'qa_manager', 'engineer', 'quality_analyst'] },
      ]
    },
    {
      id: 'quality',
      label: 'Quality Management',
      items: [
        { name: 'KPI Dashboard', href: '/kpis', icon: TrendingUp, roles: ['admin', 'manager', 'quality_analyst'] },
        { name: 'CAPA Management', href: '/capas', icon: ClipboardList, roles: ['admin', 'manager', 'engineer', 'quality_analyst'] },
        { name: 'ONCR Management', href: '/oncrs', icon: Factory, roles: ['admin', 'manager', 'engineer', 'quality_analyst', 'technician'] },
        { name: 'NCR Management', href: '/ncrs', icon: AlertTriangle, roles: ['admin', 'manager', 'engineer', 'quality_analyst'] },
        { name: 'Vendor Management', href: '/vendors', icon: Truck, roles: ['admin', 'manager', 'quality_analyst'] },
      ]
    },
    {
      id: 'compliance',
      label: 'Compliance & Audit',
      items: [
        { name: 'Document Control', href: '/documents', icon: FileText, roles: ['admin', 'manager', 'engineer', 'technician', 'quality_analyst'] },
        { name: 'BMS Administration', href: '/bms-admin', icon: FileText, roles: ['admin', 'qa_manager', 'manager', 'engineer', 'quality_analyst', 'technician', 'user', 'viewer', 'requester', 'employee'] },
        { name: 'Change Management', href: '/change-management', icon: GitBranch, roles: ['admin', 'manager', 'engineer', 'quality_analyst'] },
        { name: 'Document Portal', href: '/document-portal', icon: FolderOpen, roles: ['admin', 'manager', 'engineer', 'technician', 'quality_analyst', 'viewer', 'requester', 'employee'] },
      ]
    },
    {
      id: 'operations',
      label: 'Operations',
      items: [
        { name: 'Equipment', href: '/equipment', icon: Wrench, roles: ['admin', 'manager', 'technician'] },
        { name: 'Certifications', href: '/certifications', icon: Award, roles: ['admin', 'manager', 'engineer', 'technician'] },
        { name: 'Employees', href: '/employees', icon: Users, roles: ['admin', 'manager'] },
        { name: 'Tasks', href: '/tasks', icon: CheckSquare, roles: ['admin', 'manager', 'engineer', 'technician', 'quality_analyst', 'employee'] },
        { name: 'Feedback', href: '/feedback', icon: MessageSquare, roles: ['admin', 'manager', 'engineer', 'technician', 'quality_analyst', 'employee'] },
      ]
    },
    {
      id: 'admin',
      label: 'Administration',
      items: [
        { name: 'Command Center', href: '/command-center', icon: Command, roles: ['admin', 'qa_manager', 'manager'] },
        { name: 'Email Settings', href: '/email-settings', icon: Mail, roles: ['admin', 'qa_manager'] },
        { name: 'Admin Control', href: '/admin', icon: Settings, roles: ['admin'] },
        { name: 'Reports', href: '/reports', icon: FileCheck, roles: ['admin', 'manager', 'quality_analyst'] },
      ]
    },
  ];
 
  const userRole = user?.role || 'employee';
 
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 shadow-lg transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo Header */}
        <div className="flex items-center justify-between h-16 sm:h-20 px-3 sm:px-4 bg-gradient-to-r from-blue-700 to-blue-800 border-b border-blue-800">
          <div className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_certtrack-13/artifacts/sw1i68s3_aecf4cf2-9a0b-4e5f-938a-f9a56fcb11e7.png"
              alt="MCG"
              className="h-10 sm:h-12 w-auto brightness-0 invert"
            />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-blue-100 hover:text-white p-1"
            data-testid="close-sidebar-btn"
          >
            <X size={22} />
          </button>
        </div>
 
        <nav className="mt-2 px-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {navGroups.map((group) => {
            const filteredItems = group.items.filter(item =>
              item.roles.includes(userRole)
            );
            if (filteredItems.length === 0) return null;
 
            // No label means top-level items (Dashboard)
            if (!group.label) {
              return filteredItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2.5 mb-1 rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon size={18} className="mr-3" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </Link>
                );
              });
            }
 
            const isExpanded = expandedGroups[group.id] !== false;
            const hasActiveItem = filteredItems.some(item => location.pathname === item.href);
 
            return (
              <div key={group.id} className="mb-1">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${
                    hasActiveItem ? 'text-blue-700 bg-blue-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>{group.label}</span>
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isExpanded && filteredItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center px-3 py-2 ml-1 mb-0.5 rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon size={16} className="mr-3" />
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
 
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
              <User size={16} className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-slate-500 capitalize">{(user?.role || 'employee').replace('_', ' ')}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Sign Out
          </button>
        </div>
      </div>
 
      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Top bar */}
        <div className="h-14 sm:h-16 bg-white border-b border-slate-200 flex items-center justify-between px-3 sm:px-6 shadow-sm">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-500 hover:text-blue-600 transition-colors p-2"
            data-testid="toggle-sidebar-btn"
          >
            <Menu size={24} />
          </button>
 
          <div className="flex items-center space-x-2 sm:space-x-4">
            {isAdmin && (
              <div className="relative">
                <button
                  className="relative text-slate-500 hover:text-blue-600 transition-colors p-2"
                  data-testid="notifications-btn"
                  onClick={() => setShowAlertDropdown(!showAlertDropdown)}
                  title={`${alertCount} unread alerts`}
                >
                  <Bell size={22} />
                  {alertCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-600 text-white text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center text-[10px] sm:text-xs">
                      {alertCount > 99 ? '99+' : alertCount}
                    </span>
                  )}
                </button>

                {/* Alert Dropdown */}
                {showAlertDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
                    <div className="p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-800">Quality Alerts</h3>
                        {alertCount > 0 && (
                          <button
                            onClick={handleClearAllAlerts}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {alerts.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                          No active alerts
                        </div>
                      ) : (
                        alerts.map((alert) => (
                          <div key={alert.id} className="p-3 border-b border-slate-100 hover:bg-slate-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center mb-1">
                                  <AlertTriangle size={14} className={`mr-2 ${
                                    alert.severity === 'high' ? 'text-red-500' :
                                    alert.severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                                  }`} />
                                  <span className="text-xs font-medium text-slate-600 uppercase">
                                    {alert.category}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-800 mb-1">{alert.title}</p>
                                <p className="text-xs text-slate-600">{alert.description}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {new Date(alert.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <button
                                onClick={() => handleDismissAlert(alert.id)}
                                className="text-slate-400 hover:text-slate-600 ml-2"
                                title="Dismiss alert"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Backdrop to close dropdown */}
                {showAlertDropdown && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowAlertDropdown(false)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
 
        {/* Page content */}
        <main className="p-3 sm:p-4 md:p-6 bg-slate-50 min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
 
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};
 
export default Layout;
